// Client-side twin of the locate step in lens-bundle's WebLensArchiveInspector, feeding the
// dropzone's lens-budget meter. It reads only the END of the zip: the end-of-central-directory
// record and the central directory, which already carry every entry's name and compressed size —
// no entry data is read and nothing is decompressed, so measuring a 100MB archive costs a few
// small slice reads of the local file.
//
// Guidance only: the conversion re-compresses entries, so the packed bundle can differ slightly
// from this sum, and the server stays authoritative. Anything unexpected (not a zip, zip64
// markers, no build output) yields null and the meter simply does not render.

// Mirrors lens-bundle WebLensConstants.BUILD_DIR_CANDIDATES (same priority order) and INDEX_HTML.
const BUILD_DIR_CANDIDATES = ["dist", "build", "out", "www"];
const INDEX_HTML = "index.html";

const EOCD_SIGNATURE = 0x06_05_4b_50;
const CENTRAL_HEADER_SIGNATURE = 0x02_01_4b_50;
const EOCD_MIN_BYTES = 22;
const MAX_COMMENT_BYTES = 0xff_ff;
const ZIP64_16 = 0xff_ff;
const ZIP64_32 = 0xff_ff_ff_ff;

export interface WebLensBuildOutputMeasurement {
    /** Entry-name prefix of the located build output; "" when it sits at the archive root. */
    builtPrefix: string;
    /** Sum of the build output's compressed entry sizes — approximately the finished lens. */
    compressedBytes: number;
}

interface CentralEntry {
    name: string;
    compressedBytes: number;
}

/** Parses the central directory. Null for anything that is not a plain (non-zip64) zip. */
const readCentralEntries = async (file: Blob): Promise<CentralEntry[] | null> => {
    if (file.size < EOCD_MIN_BYTES) {
        return null;
    }
    const tailStart = Math.max(0, file.size - (EOCD_MIN_BYTES + MAX_COMMENT_BYTES));
    const tail = new DataView(await file.slice(tailStart).arrayBuffer());

    // The EOCD floats in front of an up-to-64KB comment; scanning backwards finds the last real
    // record, and the comment-length cross-check rejects a signature that merely appears inside
    // comment bytes.
    let eocd = -1;
    for (let i = tail.byteLength - EOCD_MIN_BYTES; i >= 0; i--) {
        if (
            tail.getUint32(i, true) === EOCD_SIGNATURE &&
            i + EOCD_MIN_BYTES + tail.getUint16(i + 20, true) === tail.byteLength
        ) {
            eocd = i;
            break;
        }
    }
    if (eocd < 0) {
        return null;
    }

    const totalEntries = tail.getUint16(eocd + 10, true);
    const directorySize = tail.getUint32(eocd + 12, true);
    const directoryOffset = tail.getUint32(eocd + 16, true);
    if (totalEntries === ZIP64_16 || directorySize === ZIP64_32 || directoryOffset === ZIP64_32) {
        return null;
    }
    if (directoryOffset + directorySize > file.size) {
        return null;
    }

    const directory = new DataView(await file.slice(directoryOffset, directoryOffset + directorySize).arrayBuffer());
    const utf8 = new TextDecoder();
    const entries: CentralEntry[] = [];
    let position = 0;
    for (let read = 0; read < totalEntries; read++) {
        if (position + 46 > directory.byteLength || directory.getUint32(position, true) !== CENTRAL_HEADER_SIGNATURE) {
            return null;
        }
        const compressedBytes = directory.getUint32(position + 20, true);
        if (compressedBytes === ZIP64_32) {
            return null;
        }
        const nameLength = directory.getUint16(position + 28, true);
        const extraLength = directory.getUint16(position + 30, true);
        const commentLength = directory.getUint16(position + 32, true);
        if (position + 46 + nameLength > directory.byteLength) {
            return null;
        }
        const name = utf8.decode(new Uint8Array(directory.buffer, directory.byteOffset + position + 46, nameLength));
        entries.push({ name, compressedBytes });
        position += 46 + nameLength + extraLength + commentLength;
    }
    return entries;
};

const isDirectoryEntry = (name: string): boolean => name.endsWith("/");

// Mirrors the inspector's isIgnored / isUnsafeName filters: these entries never reach the packed
// lens, so they must not count toward the budget either.
const isIgnoredName = (name: string): boolean => name.includes("__MACOSX") || name.endsWith(".DS_Store");

const isUnsafeName = (name: string): boolean => {
    const normalized = name.replaceAll("\\", "/");
    return normalized.startsWith("/") || /^[A-Za-z]:/.test(normalized) || normalized.split("/").includes("..");
};

/** Mirrors the inspector: one shared top-level directory is the archive root, otherwise "". */
const detectRootPrefix = (names: Iterable<string>): string => {
    let candidate: string | null = null;
    for (const name of names) {
        const slash = name.indexOf("/");
        if (slash < 0) {
            return "";
        }
        const top = name.slice(0, slash + 1);
        if (candidate === null) {
            candidate = top;
        } else if (candidate !== top) {
            return "";
        }
    }
    return candidate ?? "";
};

/** Mirrors the inspector: candidate build dirs in priority order, then the archive root. */
const locateBuiltOutput = (names: ReadonlySet<string>, rootPrefix: string): string | null => {
    for (const candidate of BUILD_DIR_CANDIDATES) {
        const prefix = rootPrefix + candidate + "/";
        if (names.has(prefix + INDEX_HTML)) {
            return prefix;
        }
    }
    if (names.has(rootPrefix + INDEX_HTML)) {
        return rootPrefix;
    }
    return null;
};

/**
 * Locates the build output the conversion would pack and sums its compressed size — the number
 * the 8MB lens budget is spent on. Null when the archive can't be read or has no build output;
 * the caller shows nothing and leaves the verdict to the server.
 */
export const measureWebLensBuildOutput = async (file: Blob): Promise<WebLensBuildOutputMeasurement | null> => {
    let entries: CentralEntry[] | null;
    try {
        entries = await readCentralEntries(file);
    } catch {
        return null;
    }
    if (!entries) {
        return null;
    }
    const kept = entries.filter(
        (entry) => !isDirectoryEntry(entry.name) && !isIgnoredName(entry.name) && !isUnsafeName(entry.name),
    );
    if (kept.length === 0) {
        return null;
    }
    const names = new Set(kept.map((entry) => entry.name));
    const builtPrefix = locateBuiltOutput(names, detectRootPrefix(names));
    if (builtPrefix === null) {
        return null;
    }
    const compressedBytes = kept
        .filter((entry) => entry.name.startsWith(builtPrefix))
        .reduce((sum, entry) => sum + entry.compressedBytes, 0);
    return { builtPrefix, compressedBytes };
};
